import { CommonSortModelResource } from '@/api/resources/common/shared/models/common.models'
import {
    ColDef,
    ColGroupDef,
    Column,
    DateFilterModel,
    GridApi,
    NumberFilterModel,
    RowNode,
    SetFilterModel,
    TextFilterModel
} from '@ag-grid-enterprise/all-modules'
import { SetFilterModelValue } from '@ag-grid-enterprise/set-filter/dist/cjs/setFilter/setFilterModel'
import { store } from '@/store/store'
import { VIRTUAL_SHELF_OPTIONS_GROSSING_SAMPLE } from '@/modules/grossingModule/services/grossingSampleService'
import i18n from '@/lang/i18n'
import { isPlainObject } from 'lodash'
import { ViewSizeMode } from '@/shared/models/ag-grid.model'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { DateUtils } from '@/shared/util/date-utils'
import moment from 'moment'
import { SidebarTimeFilterType } from '@/components/sideBar/filters/time-filter/sidebar-filters-time-filter.model'

export class GridUtils {
    static getSortValue(sortModel?: CommonSortModelResource[]): string[] {
        if (sortModel?.length) {
            return sortModel.map(i => `${i.colId},${i.sort}`)
        }
        return []
    }

    static changeRowHeightGrid(gridApi: GridApi, size: number): void {
        gridApi?.forEachNode((rowNode: RowNode) => {
            const rowNodeHeight = rowNode.rowHeight ?? 0
            if (rowNode.data) {
                if (rowNodeHeight > ViewSizeMode.NORMAL) {
                    rowNode.setRowHeight(rowNodeHeight)
                } else {
                    rowNode.setRowHeight(size)
                }
            }
        })
        gridApi?.onRowHeightChanged()
    }

    static filterPatientColumns(columns: (ColDef | ColGroupDef)[], middleNameId: string, surnameTwoId: string): (ColDef | ColGroupDef)[] {
        return columns.filter(column => {
            if ('children' in column) {
                column.children = GridUtils.filterPatientColumns(column.children, middleNameId, surnameTwoId)
                return true
            }
            return (column.field === middleNameId && store.getters.parameters.global?.middleNameSupported) ||
                (column.field === surnameTwoId && store.getters.parameters.global?.secondSurnameSupported) ||
                (column.field !== surnameTwoId && column.field !== middleNameId)
        })
    }

    static processCellOutput(column: Column, value: any, node?: RowNode): string {
        const hasCellRenderer = typeof column.getColDef().cellRenderer === 'function'
        const hasValueFormatter = typeof column.getColDef().valueFormatter === 'function'
        const columnId = column.getColId()
        if (columnId === 'fragmentOrientation') {
            return value.forDescription
        } else if (columnId === 'fragmentOrientation.forDescription') {
            return value
        } else if (columnId === 'samVirtualShelf') {
            const virtualShelfOption = VIRTUAL_SHELF_OPTIONS_GROSSING_SAMPLE.find(virtualShelfOption => virtualShelfOption.key === value)
            return virtualShelfOption ? i18n.tc(virtualShelfOption.label) : ''
        } else {
            if (hasCellRenderer) {
                const cellRendererCallback: any = column.getColDef().cellRenderer
                if (value) {
                    return cellRendererCallback({ value: value })
                } else if (node) {
                    return cellRendererCallback(node)
                }
                return ''
            } else if (hasValueFormatter) {
                const valueFormatterCallback: any = column.getColDef().valueFormatter
                return valueFormatterCallback({ value: value })
            } else if (isPlainObject(value)) {
                return value.name ?? value.translatedName
            }
            return value
        }
    }

    static keepSelectionGridWhenSearch(gridApi: GridApi, idDataNode: number, previousNodes: RowNode[], prop: string = 'id'): void {
        gridApi?.forEachNode(node => {
            if (node.data[prop] === idDataNode) {
                node.setSelected(true)
            } else {
                const foundNode = previousNodes.find((findNode) => node?.data[prop] === findNode?.data[prop])
                if (foundNode) {
                    node.setSelected(foundNode.isSelected()!)
                } else {
                    node.setSelected(false)
                }
            }
        })
    }

    /// Filter model utilities

    static getDateFilterModel(type: string, dateFrom?: Nullable<Date>, dateTo?: Nullable<Date>): DateFilterModel {
        return {
            dateFrom: dateFrom ? DateUtils.formatDate(dateFrom) : null,
            dateTo: dateTo ? DateUtils.formatDate(dateTo) : null,
            filterType: 'date',
            type
        }
    }
    static getDateTimeFilterModel(type: string, dateFrom?: Nullable<Date>, dateTo?: Nullable<Date>): DateFilterModel {
        return {
            dateFrom: dateFrom ? DateUtils.formatDate(dateFrom, DateUtils.dateTimeWithSecondsPattern) : null,
            dateTo: dateTo ? DateUtils.formatDate(dateTo, DateUtils.dateTimeWithSecondsPattern) : null,
            filterType: 'date',
            type
        }
    }
    static getNumberFilterModel(type: string, filter?: Nullable<number>, filterTo?: Nullable<number>): NumberFilterModel {
        return {
            filter,
            filterTo,
            filterType: 'number',
            type
        }
    }
    static getSetFilterModel(values: SetFilterModelValue): SetFilterModel {
        return {
            filterType: 'set',
            values
        }
    }
    static getTextFilterModel(type: string, filter?: Nullable<string>): TextFilterModel {
        return {
            filter,
            filterType: 'text',
            type
        }
    }

    static getTodayDateFilterModel(): DateFilterModel {
        return GridUtils.getDateFilterModel('equals', new Date())
    }
    static getThisWeekDateFilterModel(): DateFilterModel {
        return GridUtils.getDateFilterModel(
            'inRange',
            moment().startOf('week').toDate(),
            moment().endOf('week').toDate())
    }
    static getThisMonthDateFilterModel(): DateFilterModel {
        return GridUtils.getDateFilterModel(
            'inRange',
            moment().startOf('month').toDate(),
            moment().endOf('month').toDate())
    }
    static getThisQuarterDateFilterModel(): DateFilterModel {
        return GridUtils.getDateFilterModel(
            'inRange',
            moment().startOf('quarter').toDate(),
            moment().endOf('quarter').toDate())
    }
    static getThisYearDateFilterModel(): DateFilterModel {
        return GridUtils.getDateFilterModel(
            'inRange',
            moment().startOf('year').toDate(),
            moment().endOf('year').toDate())
    }
    static getTimeFilterModel(timeFilter: SidebarTimeFilterType): StrictNullable<DateFilterModel> {
        switch (timeFilter) {
        case SidebarTimeFilterType.TODAY:
            return GridUtils.getTodayDateFilterModel()
        case SidebarTimeFilterType.THIS_WEEK:
            return GridUtils.getThisWeekDateFilterModel()
        case SidebarTimeFilterType.THIS_MONTH:
            return GridUtils.getThisMonthDateFilterModel()
        case SidebarTimeFilterType.THIS_QUARTER:
            return GridUtils.getThisQuarterDateFilterModel()
        case SidebarTimeFilterType.THIS_YEAR:
            return GridUtils.getThisYearDateFilterModel()
        default:
            return null
        }
    }
}
